import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { ChevronDown, ChevronRight, Tag, FileText } from "lucide-react";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import axiosInstance from "../../../axiosInstance";
import { toast } from "react-toastify";
import { useAuth } from "../../../contexts/AuthContext";
import AccessDenied from "../../AccessDenied";
import "./Category.scss";

const Category = ({ markPageAsCompleted }) => {
  // State management
  const [categories, setCategories] = useState([]);
  const [editableCategories, setEditableCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  // eslint-disable-next-line
  const [selectAll, setSelectAll] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [isEditing, setIsEditing] = useState(false);
  // eslint-disable-next-line
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [popupAction, setPopupAction] = useState(() => () => {});
  const [initialValues, setInitialValues] = useState({
    id: null,
    name: "",
    description: "",
    tags: "",
    sortOrder: 0,
    subCategories: [],
  });

  const { user } = useAuth();

  // ---- Sidebar collapse (whole aside) — persisted so it stays how the
  // user left it across reloads -------------------------------------
  const SIDEBAR_COLLAPSED_KEY = "categories_sidebar_collapsed";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage unavailable (e.g. private mode) — fail silently
      }
      return next;
    });
  };

  // ---- "How It Works" card collapse — independent of the sidebar
  // collapse above, also persisted -------------------------------------
  const INFO_CARD_COLLAPSED_KEY = "categories_info_card_collapsed";
  const [isInfoCardCollapsed, setIsInfoCardCollapsed] = useState(() => {
    try {
      return localStorage.getItem(INFO_CARD_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleInfoCard = () => {
    setIsInfoCardCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INFO_CARD_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage unavailable — fail silently
      }
      return next;
    });
  };

  // Derived, real-data metric stats (no fabricated numbers)
  const categoryStats = useMemo(() => {
    let subCount = 0;
    let taggedCount = 0;
    const walk = (nodes, isTopLevel) => {
      nodes.forEach((node) => {
        if (!isTopLevel) subCount += 1;
        if (node.tags && node.tags.toString().trim() !== "") {
          taggedCount += 1;
        }
        if (node.subCategories && node.subCategories.length > 0) {
          walk(node.subCategories, false);
        }
      });
    };
    walk(categories, true);
    return {
      total: categories.length,
      subCategories: subCount,
      tagged: taggedCount,
      selected: selectedCategories.length,
    };
  }, [categories, selectedCategories]);

  // Access control
  const allowedRoles = [
    "ROLE_CRM-ADMIN",
    "ROLE_INVENTORY-MANAGER",
    "ROLE_ERP-ADMIN",
  ];
  const hasAccess = user?.authorities?.some((role) =>
    allowedRoles.includes(role),
  );

  // API base URL determination
  const getApiBaseUrl = useCallback(() => {
    if (user?.authorities?.includes("ROLE_ERP-ADMIN"))
      return process.env.REACT_APP_API_ERP_ADMIN_URL;
    if (user?.authorities?.includes("ROLE_CRM-ADMIN"))
      return process.env.REACT_APP_API_CRM_ADMIN_URL;
    if (user?.authorities?.includes("ROLE_INVENTORY-MANAGER"))
      return process.env.REACT_APP_API_INV_MANAGER_URL;
    return null;
  }, [user?.authorities]);

  // Validation schema
  const categoryValidationSchema = Yup.object().shape({
    name: Yup.string().required("Category Name is required"),
    description: Yup.string(),
    tags: Yup.string(),
    sortOrder: Yup.number().min(0, "Sort order must be non-negative").integer(),
    subCategories: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Subcategory name is required"),
        description: Yup.string(),
        tags: Yup.string(),
        sortOrder: Yup.number().min(0).integer(),
        subCategories: Yup.array().of(
          Yup.object().shape({
            name: Yup.string().required("Sub-subcategory name is required"),
            description: Yup.string(),
            tags: Yup.string(),
            sortOrder: Yup.number().min(0).integer(),
          }),
        ),
      }),
    ),
  });

  // API Functions
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`${getApiBaseUrl()}/categories`);
      const categoriesData =
        response.data?.data?.map((category) => ({
          id: category.id || "N/A",
          categoryCode: category.categoryCode || "",
          name: category.name || "Unknown",
          description: category.description || "",
          firstLayer: category.firstLayer || false,
          lastLayer: category.lastLayer || false,
          tags: category.tags || "",
          sortOrder: category.sortOrder || 0,
          subCategories: category.subCategories || [],
        })) || [];

      setCategories(categoriesData);
      setEditableCategories(categoriesData.map((c) => ({ ...c })));

      if (categoriesData.length > 0) {
        markPageAsCompleted("Product Category");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [markPageAsCompleted, getApiBaseUrl]);

  const formatSubCategoriesForAPI = (subCategories, level = 1) => {
    return subCategories.map((subCategory, index) => ({
      id: subCategory.id || null,
      name: subCategory.name,
      description: subCategory.description || "",
      tags: subCategory.tags || "",
      sortOrder: level * 1000 + (index + 1),
      subCategories: subCategory.subCategories
        ? formatSubCategoriesForAPI(subCategory.subCategories, level + 1)
        : [],
    }));
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const mainSortOrder = categories.length + 1;

      const payload = {
        name: values.name,
        description: values.description || "",
        tags: values.tags || "",
        sortOrder: isEditing ? values.sortOrder : mainSortOrder,
        subCategories: formatSubCategoriesForAPI(values.subCategories || []),
      };

      if (isEditing) {
        await axiosInstance.put(
          `${getApiBaseUrl()}/categories/${values.id}`,
          payload,
        );
        toast.success("Category updated successfully.");
      } else {
        await axiosInstance.post(`${getApiBaseUrl()}/categories`, payload);
        toast.success("Category created successfully.");
      }

      fetchCategories();
      resetForm();
      setIsEditing(false);
      setShowForm(false);
    } catch (error) {
      console.error("Error submitting category:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (categoryId) => {
    try {
      await axiosInstance.delete(`${getApiBaseUrl()}/categories/${categoryId}`);
      toast.success("Category deleted successfully.");
      fetchCategories();
      markPageAsCompleted("Product Category");
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };

  const handleBatchDelete = () => {
    if (selectedCategories.length === 0) {
      toast.warning("Please select at least one category to delete");
      return;
    }

    setPopupAction(() => async () => {
      try {
        await Promise.all(
          selectedCategories.map((categoryId) =>
            axiosInstance.delete(`${getApiBaseUrl()}/categories/${categoryId}`),
          ),
        );
        toast.success(
          `Successfully deleted ${selectedCategories.length} category(ies)`,
        );
        setSelectedCategories([]);
        // setSelectAll(false);
        fetchCategories();
        markPageAsCompleted("Product Category");
      } catch (error) {
        console.error("Error deleting categories:", error);
      } finally {
        setShowWarningPopup(false);
      }
    });
    setShowWarningPopup(true);
  };

  const handleInlineEdit = (categoryId, field, value) => {
    setEditableCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId ? { ...category, [field]: value } : category,
      ),
    );
  };

  // Tree view functions
  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const toggleSelectCategory = (categoryId, event) => {
    event.stopPropagation();
    const newSelected = selectedCategories.includes(categoryId)
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];
    setSelectedCategories(newSelected);
  };

  const expandAll = () => {
    const allIds = new Set();
    const collectIds = (cats) => {
      cats.forEach((cat) => {
        if (cat.subCategories && cat.subCategories.length > 0) {
          allIds.add(cat.id);
          collectIds(cat.subCategories);
        }
      });
    };
    collectIds(categories);
    setExpandedNodes(allIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const onselectAll = () => {
    const allIds = [];
    const collectIds = (cats) => {
      cats.forEach((cat) => {
        allIds.push(cat.id);
        if (cat.subCategories) collectIds(cat.subCategories);
      });
    };
    collectIds(categories);
    setSelectedCategories(allIds);
  };

  const clearSelection = () => {
    setSelectedCategories([]);
  };

  const handleEditCategory = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    if (category) {
      setIsEditing(true);
      setInitialValues({
        id: category.id,
        name: category.name,
        description: category.description,
        tags: category.tags,
        sortOrder: category.sortOrder,
        subCategories: category.subCategories || [],
      });
      setShowForm(true);
    }
  };

  const handleAddCategory = () => {
    setInitialValues({
      id: null,
      name: "",
      description: "",
      tags: "",
      sortOrder: 0,
      subCategories: [],
    });
    setShowForm(true);
    setIsEditing(false);
  };

  // Effects
  useEffect(() => {
    if (!hasAccess) return;
    fetchCategories();
  }, [fetchCategories, hasAccess]);

  useEffect(() => {
    const firstLevelIds = categories
      .filter((cat) => cat.subCategories && cat.subCategories.length > 0)
      .map((cat) => cat.id);
    setExpandedNodes(new Set(firstLevelIds));
  }, [categories]);

  useEffect(() => {
    const allCategoryIds = categories.map((category) => category.id);
    const allSelected =
      allCategoryIds.length > 0 &&
      allCategoryIds.every((id) => selectedCategories.includes(id));
    setSelectAll(allSelected);
  }, [selectedCategories, categories]);

  // Category Node Component
  const CategoryNode = ({ category, level = 0, parentPath = "" }) => {
    const hasChildren =
      category.subCategories && category.subCategories.length > 0;
    const isExpanded = expandedNodes.has(category.id);
    const isSelected = selectedCategories.includes(category.id);
    const currentPath = parentPath
      ? `${parentPath} > ${category.name}`
      : category.name;

    return (
      <div className="category-node">
        <div
          className={`category-item category-item--level-${level % 4} ${
            isSelected ? "category-item--selected" : ""
          }`}
          onClick={() => hasChildren && toggleNode(category.id)}
        >
          <div className="category-item__level-indicator"></div>
          <div className="category-item__content">
            <div className="category-item__main">
              <div className="category-item__toggle">
                {hasChildren ? (
                  <button
                    className="toggle-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleNode(category.id);
                    }}
                  >
                    {isExpanded ? (
                      <ChevronDown size={20} />
                    ) : (
                      <ChevronRight size={20} />
                    )}
                  </button>
                ) : (
                  <div className="toggle-btn toggle-btn--leaf">
                    <div className="leaf-indicator"></div>
                  </div>
                )}
                <ColoredCheckbox
                  name={`category-${category.id}`}
                  color="green"
                  checked={isSelected}
                  onChange={(e) => toggleSelectCategory(category.id, e)}
                  value={category.id}
                />
              </div>

              <div className="category-item__info">
                <div className="category-item__header">
                  {bulkUpdateEnabled ? (
                    <input
                      type="text"
                      value={category.name}
                      onChange={(e) =>
                        handleInlineEdit(category.id, "name", e.target.value)
                      }
                      className="inline-edit-input"
                      style={{
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        padding: "4px 8px",
                        fontSize: "14px",
                        minWidth: "120px",
                      }}
                    />
                  ) : (
                    <h3 className="category-item__name">{category.name}</h3>
                  )}
                </div>
                <div className="category-item__details">
                  <div className="detail-item">
                    <FileText size={14} />
                    <span className="detail-item__text">
                      {bulkUpdateEnabled ? (
                        <input
                          type="text"
                          value={category.description}
                          onChange={(e) =>
                            handleInlineEdit(
                              category.id,
                              "description",
                              e.target.value,
                            )
                          }
                          className="inline-edit-input"
                          style={{
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            padding: "4px 8px",
                            fontSize: "12px",
                            minWidth: "100px",
                          }}
                        />
                      ) : (
                        category.description || "N/A"
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <Tag size={14} />
                    <span className="detail-item__text">
                      {bulkUpdateEnabled ? (
                        <input
                          type="text"
                          value={category.tags}
                          onChange={(e) =>
                            handleInlineEdit(
                              category.id,
                              "tags",
                              e.target.value,
                            )
                          }
                          className="inline-edit-input"
                          style={{
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            padding: "4px 8px",
                            fontSize: "12px",
                            minWidth: "80px",
                          }}
                        />
                      ) : (
                        category.tags || "N/A"
                      )}
                    </span>
                  </div>
                </div>
                {hasChildren && (
                  <div className="category-item__children-count">
                    {category.subCategories.length} subcategory(ies)
                  </div>
                )}
              </div>
            </div>

            <div className="category-item__actions">
              {level === 0 && (
                <>
                  <IconButton
                    icon="iconamoon:edit-light"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditCategory(category.id);
                    }}
                    variant="edit"
                    title="Edit Category"
                  />
                  <IconButton
                    icon="fluent:delete-28-regular"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(category.id);
                    }}
                    variant="delete"
                    title="Delete Category"
                  />
                </>
              )}
            </div>
          </div>

          {isSelected && level > 0 && (
            <div className="category-item__breadcrumb">Path: {currentPath}</div>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className="category-children">
            {category.subCategories.map((child) => (
              <CategoryNode
                key={child.id}
                category={child}
                level={level + 1}
                parentPath={currentPath}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!hasAccess) {
    return <AccessDenied />;
  }

  const currentCategories = bulkUpdateEnabled ? editableCategories : categories;

  return (
    <div className="operation-categories">
      {/* Metric summary tiles - derived from real category data */}
      <div className="operation-categories__metrics-grid">
        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Total Categories</span>
            <span className="metric-tile__value">{categoryStats.total}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--neutral">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 2 2 7l10 5 10-5-10-5Zm0 10L2 7v10l10 5 10-5V7l-10 5Z"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Subcategories</span>
            <span className="metric-tile__value">
              {categoryStats.subCategories}
            </span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--info">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Tagged Categories</span>
            <span className="metric-tile__value">{categoryStats.tagged}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--success">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20.6 12.6 12.6 4.6a2 2 0 0 0-1.4-.6H5a1 1 0 0 0-1 1v6.2a2 2 0 0 0 .6 1.4l8 8a2 2 0 0 0 2.8 0l5.2-5.2a2 2 0 0 0 0-2.8Z"
              />
              <circle cx="8" cy="8" r="1" />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Selected</span>
            <span className="metric-tile__value">{categoryStats.selected}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--warning">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m5 13 4 4L19 7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Helpful tip */}
      <div className="operation-categories__tip-banner">
        <span className="operation-categories__tip-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 18h6M10 22h4M12 2a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.3V16h6v-1.2c0-.9.4-1.7 1-2.3A6 6 0 0 0 12 2Z"
            />
          </svg>
        </span>
        <p>
          <strong>Tip:</strong> Build categories up to three levels deep. Nest
          related subcategories together so products stay easy to find and
          filter.
        </p>
      </div>

      <div className="category-top-bar">
        <div className="operation-categories__actions">
          <button
            type="button"
            className={`action-card ${showForm ? "action-card--active" : ""}`}
            onClick={() => {
              if (showForm) {
                setShowForm(false);
              } else {
                handleAddCategory();
              }
            }}
          >
            <span className="action-card-icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="icon"
                width="16"
                height="16"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d={showForm ? "M6 18 18 6M6 6l12 12" : "M12 4v16m8-8H4"}
                />
              </svg>
            </span>
            <span className="action-card-copy">
              <span className="action-card-title">
                {showForm ? "Close Category Form" : "Create Category"}
              </span>
              <span className="action-card-desc">
                {showForm
                  ? "Hide the category form"
                  : "Add a new top-level category"}
              </span>
            </span>
          </button>

          <button
            type="button"
            className={`action-card action-card--danger-outline ${
              selectedCategories.length === 0 ? "action-card--disabled" : ""
            }`}
            onClick={handleBatchDelete}
            disabled={selectedCategories.length === 0}
          >
            <span className="action-card-copy">
              <span className="action-card-title">
                Bulk Delete ({selectedCategories.length})
              </span>
              <span className="action-card-desc">
                Permanently delete all selected categories
              </span>
            </span>
          </button>
        </div>

        {/* Control Panel */}
        <div className="category-controls">
          <div className="controls-left">
            <button onClick={expandAll} className="control-btn">
              Expand
            </button>
            <button onClick={collapseAll} className="control-btn">
              Collapse
            </button>
            <div className="controls-divider"></div>
            <button
              onClick={onselectAll}
              className="control-btn control-btn--success"
            >
              Select All
            </button>
            <button
              onClick={clearSelection}
              className="control-btn control-btn--danger"
            >
              Clear Selection
            </button>
          </div>
        </div>
      </div>

      <div
        className={`operation-categories__layout ${
          isSidebarCollapsed ? "operation-categories__layout--collapsed" : ""
        }`}
      >
        <div className="operation-categories__main">
          {/* Form - Styled like UserRole */}
          {showForm && (
            <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={categoryValidationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, values }) => (
                  <Form>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
                      {isEditing ? "Edit Category" : "Create New Category"}
                    </h3>

                    <div className="operation-categories-form__grid">
                      <div className="category-form-group">
                        <label className="label">Category Name *</label>
                        <Field
                          type="text"
                          name="name"
                          className="form-input"
                          placeholder="e.g., Electronics"
                        />
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="category-form-group">
                        <label className="label">Description</label>
                        <Field
                          type="text"
                          name="description"
                          className="form-input"
                          placeholder="Brief description of the category"
                        />
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="description"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="category-form-group">
                        <label className="label">Tags</label>
                        <Field
                          type="text"
                          name="tags"
                          className="form-input"
                          placeholder="electronics,gadgets,tech"
                        />
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="tags"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="category-form-group full-width">
                        <label className="label">Subcategories</label>
                        <FieldArray name="subCategories">
                          {({ push, remove }) => (
                            <div className="subcategories-container">
                              {values.subCategories?.map(
                                (subCategory, index) => (
                                  <div
                                    key={index}
                                    className="subcategory-section"
                                  >
                                    <div className="subcategory-header">
                                      <h4>Subcategory {index + 1}</h4>
                                      <IconButton
                                        icon="fluent:delete-28-regular"
                                        onClick={() => remove(index)}
                                        variant="delete"
                                      />
                                    </div>

                                    <div className="subcategory-fields">
                                      <div className="subcategory-field">
                                        <label>Name *</label>
                                        <Field
                                          name={`subCategories.${index}.name`}
                                          placeholder="e.g., Mobiles"
                                          className="form-input"
                                        />
                                        <ErrorMessage
                                          name={`subCategories.${index}.name`}
                                          component="div"
                                          className="error-message"
                                        />
                                      </div>

                                      <div className="subcategory-field">
                                        <label>Description</label>
                                        <Field
                                          name={`subCategories.${index}.description`}
                                          placeholder="Description"
                                          className="form-input"
                                        />
                                      </div>

                                      <div className="subcategory-field">
                                        <label>Tags</label>
                                        <Field
                                          name={`subCategories.${index}.tags`}
                                          placeholder="phones,mobiles"
                                          className="form-input"
                                        />
                                      </div>
                                    </div>

                                    {/* Nested subcategories */}
                                    <FieldArray
                                      name={`subCategories.${index}.subCategories`}
                                    >
                                      {({
                                        push: pushNested,
                                        remove: removeNested,
                                      }) => (
                                        <div className="nested-subcategories">
                                          {values.subCategories[
                                            index
                                          ]?.subCategories?.map(
                                            (nestedSub, nestedIndex) => (
                                              <div
                                                key={nestedIndex}
                                                className="nested-subcategory"
                                              >
                                                <div className="nested-subcategory-header">
                                                  <h5>
                                                    Sub-subcategory{" "}
                                                    {nestedIndex + 1}
                                                  </h5>
                                                  <IconButton
                                                    icon="fluent:delete-28-regular"
                                                    onClick={() =>
                                                      removeNested(nestedIndex)
                                                    }
                                                    variant="delete"
                                                  />
                                                </div>

                                                <div className="nested-subcategory-fields">
                                                  <div className="subcategory-field">
                                                    <label>Name *</label>
                                                    <Field
                                                      name={`subCategories.${index}.subCategories.${nestedIndex}.name`}
                                                      placeholder="e.g., Android Phones"
                                                      className="form-input"
                                                    />
                                                    <ErrorMessage
                                                      name={`subCategories.${index}.subCategories.${nestedIndex}.name`}
                                                      component="div"
                                                      className="error-message"
                                                    />
                                                  </div>

                                                  <div className="subcategory-field">
                                                    <label>Description</label>
                                                    <Field
                                                      name={`subCategories.${index}.subCategories.${nestedIndex}.description`}
                                                      placeholder="Description"
                                                      className="form-input"
                                                    />
                                                  </div>

                                                  <div className="subcategory-field">
                                                    <label>Tags</label>
                                                    <Field
                                                      name={`subCategories.${index}.subCategories.${nestedIndex}.tags`}
                                                      placeholder="android,smartphones"
                                                      className="form-input"
                                                    />
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                          <ButtonWithIcon
                                            icon="ic:round-add"
                                            text="Add Sub-subcategory"
                                            onClick={() =>
                                              pushNested({
                                                name: "",
                                                description: "",
                                                tags: "",
                                                sortOrder: 0,
                                                subCategories: [],
                                              })
                                            }
                                            className="add-nested-btn"
                                          />
                                        </div>
                                      )}
                                    </FieldArray>
                                  </div>
                                ),
                              )}

                              <ButtonWithIcon
                                icon="ic:round-add"
                                text="Add Subcategory"
                                onClick={() =>
                                  push({
                                    name: "",
                                    description: "",
                                    tags: "",
                                    sortOrder: 0,
                                    subCategories: [],
                                  })
                                }
                                className="add-subcategory-btn"
                              />
                            </div>
                          )}
                        </FieldArray>
                      </div>

                      <div className="category-form-group button-group">
                        <ButtonWithIcon
                          type="submit"
                          icon="tdesign:send"
                          text={
                            isEditing ? "Update Category" : "Create Category"
                          }
                          disabled={isSubmitting}
                          className={
                            isEditing ? "edit-button" : "submit-button"
                          }
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}

          {/* Category Tree */}
          <div className="operation-categories__tree-section">
            <div className="category-tree">
              {currentCategories.length > 0 ? (
                currentCategories.map((category) => (
                  <CategoryNode key={category.id} category={category} />
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-state__icon">
                    <FileText size={64} />
                  </div>
                  <h3 className="empty-state__title">No categories found</h3>
                  <p className="empty-state__description">
                    Categories help organize your products into a browsable
                    structure. To get started:
                  </p>
                  <ol className="operation-categories__empty-steps">
                    <li>Create your first top-level category</li>
                    <li>Add subcategories to break it down further</li>
                    <li>Assign products to the right category</li>
                  </ol>
                  <button
                    type="button"
                    className="action-card action-card--primary"
                    onClick={handleAddCategory}
                  >
                    <span className="action-card-copy">
                      <span className="action-card-title">Create Category</span>
                      <span className="action-card-desc">
                        Add your first category
                      </span>
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          className="operation-categories__sidebar-toggle"
          onClick={toggleSidebar}
          aria-expanded={!isSidebarCollapsed}
          aria-label={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={isSidebarCollapsed ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"}
            />
          </svg>
        </button>

        <aside
          className={`operation-categories__sidebar ${
            isSidebarCollapsed ? "operation-categories__sidebar--collapsed" : ""
          }`}
          aria-hidden={isSidebarCollapsed}
        >
          <div className="sidebar-card sidebar-card--info">
            <button
              type="button"
              className={`sidebar-card__header ${
                !isInfoCardCollapsed ? "is-expanded" : ""
              }`}
              onClick={toggleInfoCard}
              aria-expanded={!isInfoCardCollapsed}
            >
              <h4>How It Works</h4>
              <svg
                className={`sidebar-card__chevron ${
                  isInfoCardCollapsed ? "is-collapsed" : ""
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m6 9 6 6 6-6"
                />
              </svg>
            </button>

            <div
              className={`sidebar-card__body ${
                isInfoCardCollapsed ? "is-collapsed" : ""
              }`}
            >
              <div className="sidebar-card__body-inner">
                <ol className="sidebar-steps">
                  <li>
                    <strong>Create Category</strong>
                    <span>
                      Add a top-level category with a name, description and
                      tags.
                    </span>
                  </li>
                  <li>
                    <strong>Nest Subcategories</strong>
                    <span>
                      Add up to two more levels to organize related products.
                    </span>
                  </li>
                  <li>
                    <strong>Assign Products</strong>
                    <span>
                      Products are grouped under the category they belong to.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div className="sidebar-card sidebar-card--success">
            <h4>Best Practices</h4>
            <ul className="sidebar-list">
              <li>Keep category names short and specific</li>
              <li>Use tags to make categories easier to filter</li>
              <li>Avoid nesting more than three levels deep</li>
              <li>Review and merge overlapping categories regularly</li>
            </ul>
          </div>

          <div className="sidebar-card sidebar-card--warning">
            <h4>Important Note</h4>
            <p>
              Deleting a category is permanent and cannot be undone. Any
              subcategories and product assignments beneath it are removed as
              well.
            </p>
          </div>
        </aside>
      </div>

      {/* Warning Popup */}
      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => popupAction()}
        onDecline={() => setShowWarningPopup(false)}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selectedCategories.length} selected categor${
          selectedCategories.length === 1 ? "y" : "ies"
        }? This action is permanent and cannot be undone. Any subcategories beneath ${
          selectedCategories.length === 1 ? "it" : "them"
        } will also be deleted.`}
        acceptText="Yes, Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default Category;
